/**
 * Firebase initialization and configuration
 */
import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getDatabase } from 'firebase/database'
import { getAnalytics, isSupported } from 'firebase/analytics'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import env from '../config/env.js'

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: env.firebase.apiKey,
  authDomain: env.firebase.authDomain,
  databaseURL: env.firebase.databaseURL,
  projectId: env.firebase.projectId,
  storageBucket: env.firebase.storageBucket,
  messagingSenderId: env.firebase.messagingSenderId,
  appId: env.firebase.appId,
  measurementId: env.firebase.measurementId,
}

// Functions region (must match backend)
const FUNCTIONS_REGION = 'europe-west2'

// Initialize Firebase
let app = null
let auth = null
let db = null
let rtdb = null
let storage = null
let analytics = null
let functions = null

/**
 * Initialize Firebase services
 * Call this once at app startup
 */
export async function initializeFirebase() {
  if (app) {
    console.warn('[Firebase] Already initialized')
    return { app, auth, db, rtdb, storage, analytics, functions }
  }

  try {
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
    db = getFirestore(app)
    rtdb = getDatabase(app)
    storage = getStorage(app)

    // Initialize Analytics only if supported (not in SSR/Node)
    if (await isSupported()) {
      analytics = getAnalytics(app)
    }

    // Initialize Functions
    functions = getFunctions(app, FUNCTIONS_REGION)

    // Connect to emulators only when explicitly enabled
    if (env.useEmulators) {
      // connectFunctionsEmulator(functions, 'localhost', 5001)
      // Uncomment these if you also run Auth/Firestore emulators:
      // connectFirestoreEmulator(db, 'localhost', 8080)
      // connectAuthEmulator(auth, 'http://localhost:9099')
    }

    // if(true){
    //    connectFunctionsEmulator(functions, 'localhost', 5001)
    // }

    return { app, auth, db, rtdb, storage, analytics, functions }
  } catch (error) {
    console.error('[Firebase] Initialization failed:', error)
    throw error
  }
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth() {
  if (!auth) {
    throw new Error('[Firebase] Not initialized. Call initializeFirebase() first.')
  }
  return auth
}

/**
 * Get Firestore instance
 */
export function getFirebaseDb() {
  if (!db) {
    throw new Error('[Firebase] Not initialized. Call initializeFirebase() first.')
  }
  return db
}

/**
 * Get Realtime Database instance
 */
export function getRealtimeDb() {
  if (!rtdb) {
    throw new Error('[Firebase] Not initialized. Call initializeFirebase() first.')
  }
  return rtdb
}

/**
 * Get Firebase Storage instance
 */
export function getFirebaseStorage() {
  if (!storage) {
    throw new Error('[Firebase] Not initialized. Call initializeFirebase() first.')
  }
  return storage
}

/**
 * Get Firebase Analytics instance
 */
export function getFirebaseAnalytics() {
  return analytics
}

/**
 * Get Firebase Functions instance
 */
export function getFirebaseFunctions() {
  if (!functions) {
    throw new Error('[Firebase] Not initialized. Call initializeFirebase() first.')
  }
  return functions
}

export { app, auth, db, rtdb, storage, analytics, functions }
